/**
 * Who is signed in, for the whole application.
 *
 * The session lives in an HttpOnly cookie, so the browser cannot read it and
 * there is nothing to persist here. "Am I signed in?" is answerable only by
 * asking the server, which is what the boot probe does — once, on mount.
 *
 * Two things are deliberate:
 *
 *   - The probe is `quiet`. A signed-out visitor's `GET /auth/me` returns 401,
 *     and routing that through the global unauthorized handler would make the
 *     very first request of a session bounce the visitor to /login... which
 *     mounts this provider, which probes again. Quiet breaks that loop: a 401
 *     from the probe is not an error, it is the answer "nobody".
 *   - Any *later* 401 is a real event — the session expired or was revoked
 *     mid-visit — so the handler clears the user and sends them to /login
 *     carrying the path they were on, so signing back in returns them to it.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api, setUnauthorizedHandler } from '../api/client';
import type { SessionUser } from '../api/types';

/** Router state carried into /login so sign-in can return where it started. */
export interface LoginRedirectState {
  from?: string;
}

interface AuthState {
  user: SessionUser | null;
  /** A session probe is in flight. True on the very first render. */
  loading: boolean;
  /** Only ever a real failure — a 401 means "signed out", not "broken". */
  error: ApiError | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // The unauthorized handler is registered once and must not be re-registered on
  // every navigation, so it reads the current location through a ref instead of
  // closing over it.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await api.get<SessionUser>('/auth/me', { quiet: true });
      setUser(me);
      setError(null);
    } catch (err) {
      setUser(null);
      // A 401 is the expected answer for a visitor with no cookie. Anything else
      // — the API being down, a 500 — is worth telling somebody about, because
      // otherwise it is indistinguishable from being signed out.
      setError(err instanceof ApiError && err.status !== 401 ? err : null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post<void>('/auth/logout');
    } catch {
      // Signing out of this browser must not depend on the network. The cookie
      // may survive a failed call, but the next request will 401 and land back
      // on /login anyway, and a customer who clicked "sign out" on a train
      // should not be told it did not work.
    }
    setUser(null);
    setError(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      const at = locationRef.current;
      // Already on the sign-in screen: clearing the user is enough, and
      // navigating would replace the ?error= the callback may have just set.
      if (at.pathname === '/login') return;
      navigate('/login', {
        replace: true,
        state: { from: `${at.pathname}${at.search}` } satisfies LoginRedirectState,
      });
    });
    return () => setUnauthorizedHandler(() => {});
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, loading, error, refresh, logout }}>{children}</AuthContext.Provider>
  );
}
